import React, { useContext } from 'react'
import { useFormContext, useFieldArray } from 'react-hook-form'

// Components
import { Typography, Box, Button } from '@mui/material'
import { CardBox, CardBoxTopBar } from '../../../theme/styleComponents'
import EntryRow from './EntryRow'

// Others
import { Add as AddIcon } from '@mui/icons-material'
import { BillingType as GeneratedBillingType, EntryType, FetchBillDetailsQuery, FeeType } from '../../../../generated'
import { EditBillForm, EditBillEntryForm } from '../../../interfaceTypes/forms'
import { AuthContext } from '../../../context'
import { getBillableStatus } from '../../../utils/entry'
import { generateUniqueId } from '../../../utils'

type Case = FetchBillDetailsQuery['fetchBillDetails']['bill']['case']
type CaseUsers = NonNullable<Case>['caseToUsers']
type BillingType = GeneratedBillingType | null | undefined
type IsFlatFeeHoursExhausted = boolean | null | undefined

interface EntrySectionProps {
  title: string
  caseId: string
  fieldName: keyof Omit<EditBillForm, 'discountPercentage' | 'isPrePaid'>
  caseUsers?: CaseUsers | null
  entryType: EntryType
  billingType: BillingType
  isFlatFeeHoursExhausted: IsFlatFeeHoursExhausted
  initialFeeTypes?: FeeType[]
  skipInitialFeeTypeFetch?: boolean
}

const EntrySection: React.FC<EntrySectionProps> = ({
  title,
  fieldName,
  caseUsers,
  entryType,
  caseId,
  billingType,
  isFlatFeeHoursExhausted,
  initialFeeTypes,
  skipInitialFeeTypeFetch
}) => {
  const { control } = useFormContext<EditBillForm>()
  const { currentUser } = useContext(AuthContext)
  const { fields, append, remove } = useFieldArray({
    control,
    name: fieldName
  })

  const handleAddEntry = () => {
    if (!currentUser || !billingType) return

    const billable = getBillableStatus({ billingType, isFlatFeeHoursExhausted, entryType })
    const newEntry: EditBillEntryForm = {
      date: new Date(),
      entryType,
      userId: '',
      description: null,
      billable,
      showOnBill: true,
      sendNotesToMatterNotes: false,
      isNew: true,
      duration: null,
      rate: 0,
      amount: null,
      qty: entryType === EntryType.Expense ? 1 : null,
      unitPrice: null,
      feeTypeId: null,
      categoryId: null,
      caseId,
      creatorId: currentUser.id,
      id: generateUniqueId()
    }
    append(newEntry)
  }

  const handleRemoveEntry = (index: number) => {
    remove(index)
  }

  return (
    <CardBox mb={3}>
      <CardBoxTopBar>
        <Typography variant="h6" fontWeight={400} color="gray900" fontSize="18px">
          {title}
        </Typography>
      </CardBoxTopBar>

      <Box sx={{ p: 3 }}>
        {fields.map(
          (field, index) =>
            billingType && (
              <EntryRow
                key={field.id}
                fieldName={`${fieldName}.${index}`}
                index={index}
                isLast={index === fields.length - 1}
                entryType={entryType}
                caseUsers={caseUsers}
                isBillableDisabled={!getBillableStatus({ billingType, isFlatFeeHoursExhausted, entryType })}
                onRemove={handleRemoveEntry}
                initialFeeTypes={initialFeeTypes}
                skipInitialFeeTypeFetch={skipInitialFeeTypeFetch}
              />
            )
        )}

        <Button startIcon={<AddIcon />} size="small" onClick={handleAddEntry}>
          Add
        </Button>
      </Box>
    </CardBox>
  )
}

export default EntrySection
